import { Link } from "react-router-dom";
import { useSiteData } from "@/context/SiteDataContext";
import { Mail, Phone, MapPin, MessageCircle, Facebook, Instagram, Linkedin } from "lucide-react";

const TikTok = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M16.5 3c.3 2.1 1.6 3.7 3.5 4v2.3c-1.3 0-2.5-.3-3.5-.9v6.2c0 3.1-2.5 5.6-5.6 5.6S5.3 17.7 5.3 14.6 7.8 9 10.9 9c.3 0 .6 0 .9.1v2.4c-.3-.1-.6-.2-.9-.2-1.7 0-3.2 1.4-3.2 3.2s1.4 3.2 3.2 3.2 3.2-1.4 3.2-3.2V3h2.4z"/></svg>
);

export default function Footer() {
  const { site: SITE, categories: CATEGORIES, services: SERVICES, waLink } = useSiteData();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-ink text-white noise" data-testid="footer">
      <div className="container-x py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
          <div className="col-span-2 md:col-span-4">
            <img src={SITE.logos.white} alt="Athena Scientific" className="h-9 w-auto" />
            <p className="mt-5 text-lg font-semibold text-white/90 max-w-xs">{SITE.tagline}</p>
            <p className="mt-4 text-sm text-white/50 max-w-xs leading-relaxed">
              A scientific solutions partner delivering reliable laboratory technologies, trusted expertise and customer-focused support.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="w-10 h-10 flex items-center justify-center rounded-md bg-white/5 hover:bg-aqua hover:text-ink transition-all"><Facebook className="w-4.5 h-4.5" strokeWidth={1.8} /></a>
              <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="w-10 h-10 flex items-center justify-center rounded-md bg-white/5 hover:bg-aqua hover:text-ink transition-all"><Instagram className="w-4.5 h-4.5" strokeWidth={1.8} /></a>
              <a href={SITE.social.tiktok} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok" aria-label="TikTok" className="w-10 h-10 flex items-center justify-center rounded-md bg-white/5 hover:bg-aqua hover:text-ink transition-all"><TikTok className="w-4.5 h-4.5" /></a>
              {SITE.social.linkedin && (
                <a href={SITE.social.linkedin} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" aria-label="LinkedIn" className="w-10 h-10 flex items-center justify-center rounded-md bg-white/5 hover:bg-aqua hover:text-ink transition-all"><Linkedin className="w-4.5 h-4.5" strokeWidth={1.8} /></a>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-widest text-aqua font-bold">Quick Links</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-white/60">
              <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/partners" className="hover:text-white transition-colors">Our Partners</Link></li>
              <li><Link to="/products" className="hover:text-white transition-colors">Our Solutions</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Blogs</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-widest text-aqua font-bold">Categories</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-white/60">
              {CATEGORIES.map((c) => (
                <li key={c.slug}><Link to={`/products?category=${c.slug}`} className="hover:text-white transition-colors">{c.name}</Link></li>
              ))}
            </ul>
            <h4 className="mt-6 text-xs uppercase tracking-widest text-aqua font-bold">Services</h4>
            <ul className="mt-4 space-y-2.5 text-sm text-white/60">
              {SERVICES.map((s) => (
                <li key={s.slug}><Link to={`/services/${s.slug}`} className="hover:text-white transition-colors">{s.name}</Link></li>
              ))}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-4">
            <h4 className="text-xs uppercase tracking-widest text-aqua font-bold">Get in Touch</h4>
            <ul className="mt-4 space-y-3.5 text-sm text-white/70">
              <li className="flex gap-3"><MapPin className="w-4.5 h-4.5 text-aqua shrink-0 mt-0.5" /><span>{SITE.company}<br />{SITE.address}</span></li>
              <li className="flex gap-3 items-center"><Phone className="w-4.5 h-4.5 text-aqua shrink-0" /><a href={`tel:${SITE.mobile}`} className="hover:text-white transition-colors">{SITE.mobile}</a></li>
              <li className="flex gap-3 items-center"><Mail className="w-4.5 h-4.5 text-aqua shrink-0" /><a href={`mailto:${SITE.email}`} className="hover:text-white transition-colors break-all">{SITE.email}</a></li>
              <li className="flex gap-3 items-center"><MessageCircle className="w-4.5 h-4.5 text-aqua shrink-0" /><a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="hover:text-white transition-colors">WhatsApp Us</a></li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-white/40">
          <p>© {year} {SITE.company}. UEN {SITE.uen}. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
